import { DRAWING_LINE_WIDTH, addDays, asIsoDate, formatPrice } from '@stockstop/shared';
import {
  applyStroke,
  distanceToPoint,
  distanceToSegment,
  drawHandle,
  drawLabel,
  extendLine,
  HANDLE_HIT_RADIUS,
  HIT_TOLERANCE,
} from './geometry.js';
import type { DrawingRenderer, Point } from './types.js';

/**
 * (3) Diagonal channel — two parallel lines with the area between them filled.
 *
 * The second line is defined by a price `offset` from the base line rather than by
 * its own anchors. That is what guarantees the two stay parallel: there is no way
 * to drag them out of alignment, because only one slope exists.
 *
 * Handles: `from` and `to` shape the base line, `offset` sets the channel width,
 * and the body moves the whole channel.
 */
export const channelRenderer: DrawingRenderer<'channel'> = {
  type: 'channel',

  handles(payload, context) {
    const { coordinates } = context;
    const fromX = coordinates.xForDate(payload.from.date);
    const fromY = coordinates.yForPrice(payload.from.price);
    const toX = coordinates.xForDate(payload.to.date);
    const toY = coordinates.yForPrice(payload.to.price);
    const offsetY = coordinates.yForPrice(payload.to.price + payload.offset);

    if (fromX === null || fromY === null || toX === null || toY === null) return [];

    const handles = [
      { id: 'from' as const, position: { x: fromX, y: fromY } },
      { id: 'to' as const, position: { x: toX, y: toY } },
    ];
    if (offsetY !== null) {
      handles.push({ id: 'offset' as never, position: { x: toX, y: offsetY } });
    }
    return handles;
  },

  draw(payload, style, label, showPrice, context) {
    const { ctx, coordinates, width } = context;
    const handles = this.handles(payload, context);
    if (handles.length < 2) return;

    const from = handles[0]!.position;
    const to = handles[1]!.position;

    /* The parallel line is the base line shifted by the offset in price space;
     * converting both endpoints keeps the shift correct on a logarithmic axis. */
    const parallelFromY = coordinates.yForPrice(payload.from.price + payload.offset);
    const parallelToY = coordinates.yForPrice(payload.to.price + payload.offset);
    if (parallelFromY === null || parallelToY === null) return;

    const bounds = { left: 0, right: width };
    const [baseStart, baseEnd] = extendLine(
      from,
      to,
      bounds,
      payload.extendLeft,
      payload.extendRight,
    );
    const [parStart, parEnd] = extendLine(
      { x: from.x, y: parallelFromY },
      { x: to.x, y: parallelToY },
      bounds,
      payload.extendLeft,
      payload.extendRight,
    );

    ctx.save();
    /* Deckkraft der gesamten Zeichnung; die Füllung rechnet zusätzlich mit ihrer
     * eigenen Durchlässigkeit. */
    ctx.globalAlpha = style.opacity;

    if (style.fillOpacity > 0) {
      ctx.globalAlpha = style.opacity * style.fillOpacity;
      ctx.fillStyle = style.color;
      ctx.beginPath();
      ctx.moveTo(baseStart.x, baseStart.y);
      ctx.lineTo(baseEnd.x, baseEnd.y);
      ctx.lineTo(parEnd.x, parEnd.y);
      ctx.lineTo(parStart.x, parStart.y);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = style.opacity;
    }

    /*
     * Die Begrenzungen sind ein Haarstrich. Beim
     * Tunnel trägt die Fläche die Aussage; kräftige Ränder würden mit den Kerzen
     * konkurrieren, die darin liegen. Bei Auswahl ein Pixel mehr, damit erkennbar
     * bleibt, woran gerade gearbeitet wird.
     */
    applyStroke(
      ctx,
      style.color,
      DRAWING_LINE_WIDTH.channel + (context.isSelected ? 1 : 0),
      style.lineStyle,
    );
    for (const [start, end] of [
      [baseStart, baseEnd],
      [parStart, parEnd],
    ] as [Point, Point][]) {
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }

    /* Beschriftung nur im Moment der Betrachtung — sonst überlädt sie den Chart. */
    if (context.isHovered || context.isSelected) {
      const caption = [
        label,
        showPrice
          ? `${formatPrice(payload.to.price, { currency: context.currency })} / ${formatPrice(
              payload.to.price + payload.offset,
              { currency: context.currency },
            )}`
          : null,
      ]
        .filter(Boolean)
        .join('  ');
      drawLabel(
        ctx,
        caption,
        { x: to.x + 8, y: Math.min(to.y, parallelToY) - 4 },
        { color: style.color },
      );
    }

    if (context.isSelected) {
      for (const handle of handles) drawHandle(ctx, handle.position, style.color);
    }
    ctx.restore();
  },

  hitTest(payload, point, context) {
    const handles = this.handles(payload, context);
    if (handles.length < 2) return null;

    for (const handle of handles) {
      if (distanceToPoint(point, handle.position) <= HANDLE_HIT_RADIUS) return handle.id;
    }

    const { coordinates, width } = context;
    const from = handles[0]!.position;
    const to = handles[1]!.position;
    const parallelFromY = coordinates.yForPrice(payload.from.price + payload.offset);
    const parallelToY = coordinates.yForPrice(payload.to.price + payload.offset);
    if (parallelFromY === null || parallelToY === null) return null;

    const bounds = { left: 0, right: width };
    const [baseStart, baseEnd] = extendLine(
      from,
      to,
      bounds,
      payload.extendLeft,
      payload.extendRight,
    );
    const [parStart, parEnd] = extendLine(
      { x: from.x, y: parallelFromY },
      { x: to.x, y: parallelToY },
      bounds,
      payload.extendLeft,
      payload.extendRight,
    );

    const onBase = distanceToSegment(point, baseStart, baseEnd) <= HIT_TOLERANCE;
    const onParallel = distanceToSegment(point, parStart, parEnd) <= HIT_TOLERANCE;
    return onBase || onParallel ? 'body' : null;
  },

  applyDrag(payload, handle, drag) {
    switch (handle) {
      case 'from':
        return { ...payload, from: { date: asIsoDate(drag.date), price: drag.price } };
      case 'to':
        return { ...payload, to: { date: asIsoDate(drag.date), price: drag.price } };
      case 'offset':
        /* Widening or narrowing: the offset is the gap at the `to` anchor. */
        return { ...payload, offset: drag.price - payload.to.price };
      case 'body':
        return {
          ...payload,
          from: {
            date: addDays(payload.from.date, drag.deltaDays),
            price: payload.from.price + drag.deltaPrice,
          },
          to: {
            date: addDays(payload.to.date, drag.deltaDays),
            price: payload.to.price + drag.deltaPrice,
          },
        };
      default:
        return null;
    }
  },
};
